import { Button, Dialog, EButtonColor, StringInput } from '@web_opxp/components';
import { FirebaseActions, StatsActions, StatsSelectors, useTranslation } from '@web_opxp/features';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action, Dispatch } from 'redux';
import { SKELETON_CLASSNAME } from '../base/constants';
import { WEEK_GOAL_STAT_ID } from './constants';
import { getDateString } from './utils';

interface ITrainingGoalDialogProps {
  weekDay: number;
  onClose: () => void;
}

export default function TrainingGoalDialog({ weekDay, onClose }: Readonly<ITrainingGoalDialogProps>) {
  const { t } = useTranslation();
  const [goal, setGoal] = useState<number>();
  const [error, setError] = useState<string>();
  const [savingStarted, setSavingStarted] = useState(false);
  const saving = useSelector(StatsSelectors.isSaving);
  const dispatch = useDispatch<Dispatch<Action<string>>>();
  const inputClass = `goal-input${saving ? ' ' + SKELETON_CLASSNAME : ''}`;
  const title = t('TrainingCalendar.GoalDialog.Title');
  const description = t('TrainingCalendar.GoalDialog.Description');
  const cta = t('TrainingCalendar.GoalDialog.CTA');
  const back = t('TrainingCalendar.GoalDialog.Back');
  const handleSubmit = () => {
    if (!goal) {
      setError(t('common.common:FormValidation.Required'));
      return;
    }
    const date = getDateString();
    dispatch(
      StatsActions.updateGenericStat(WEEK_GOAL_STAT_ID.replace('x', weekDay.toString()), {
        valueInt: goal,
        valueTxt: date,
      })
    );
    dispatch(StatsActions.saveStatsQueue());
    dispatch(
      FirebaseActions.analyticsEventAction({
        eventName: 'v_calendar_goal_input',
        parameters: [
          { key: 'event_date', value: date },
          { key: 'week_day', value: weekDay },
          { key: 'quantity', value: goal },
        ],
      })
    );
    // set saving state
    setSavingStarted(true);
    // reset error
    setError(undefined);
    // reset value
    setGoal(undefined);
  };
  const handleChange = (value: string) => {
    const numValue = Number(value);
    if (value !== '' && isNaN(numValue)) {
      setError(t('common.common:FormValidation.Required'));
      return;
    }
    if (numValue >= 1000) {
      setError(t('common.common:FormValidation.Required'));
      return;
    }
    setGoal(value !== '' ? numValue : undefined);
    setError(undefined);
  };
  const closeHandler = useCallback(() => {
    setSavingStarted(false);
    setError(undefined);
    setGoal(undefined);
    onClose();
  }, [setSavingStarted, setError, setGoal, onClose]);
  useEffect(() => {
    if (!savingStarted) {
      return;
    }
    // effect will close the dialog when saving process was started and finished
    if (!saving) {
      setSavingStarted(false);
      closeHandler();
    }
  }, [saving, savingStarted, setSavingStarted, closeHandler]);
  return (
    <Dialog
      isOpen={weekDay >= 0}
      autoFocus={true}
      usePortal={true}
      onClose={closeHandler}
      className="training-goal-dialog"
      footerClassName="goal-dialog-footer"
      footerContent={
        <>
          <Button disabled={saving} color={EButtonColor.white} label={back} onClick={closeHandler} />
          <Button disabled={saving || !!error || !goal} color={EButtonColor.blue} label={cta} onClick={handleSubmit} />
        </>
      }>
      <div className="training-goal-dialog-body">
        <h3>{title}</h3>
        <p>{description}</p>
        <StringInput
          autofocus={true}
          onChange={handleChange}
          placeholder={title}
          className={inputClass}
          error={error}
        />
      </div>
    </Dialog>
  );
}
